/* istanbul ignore file */
const TypeParser = require('@common-vue/media-card-large/src/modules/type/parsers/TypeParser');
const ColorParser = require('@common-vue/media-card-large/src/modules/color/parsers/ColorParser');
const SizeParser = require('@common-vue/media-card-large/src/modules/size/parsers/SizeParser');

/** @type {(a: Color) => boolean} */
const isWhite = (color) => color.name !== 'No Color';
/** @type {(a: Color) => boolean} */
const isNoColor = (color) => color.name === 'No Color';

/** @type {(a: Color[]) => Color | undefined} */
const findWhiteColor = (colors) => colors.find(isWhite);
/** @type {(a: Color[]) => Color[]} */
const findNoColors = (colors) => colors.filter(isNoColor);

/** @type {(a: Color[]) => string[]} */
const findNoColorIds = (colors) => findNoColors(colors).map((color) => color.id.toString());
/** @type {(a: Color[]) => string | undefined} */
const findWhiteColorId = (colors) => findWhiteColor(colors)?.id?.toString();

/**
 * Replace any color id of "No Color" with the color id of "White" for given upc
 * @param {Record<string, number>} upcMap
 * @param {string} upc
 * @param {string} whiteColorId
 * @param {string[]} noColorIds
 * @returns
 */
function convertUpc(upcMap, upc, whiteColorId, noColorIds) {
  const [upcColor, upcSize, upcType] = upc.split('_');

  if (upcColor === whiteColorId) return;

  if (noColorIds.includes(upcColor)) {
    const newUpcKey = `${whiteColorId}_${upcSize}_${upcType}`;
    upcMap[newUpcKey] = upcMap[upc];

    delete upcMap[upc];
  }
}

/**
 * Replace any color id of "No Color" with the color id of "White" withinn size's colorToTypes
 * @param {Size} size
 * @param {string} whiteColorId
 * @param {string} noColorId
 * @returns
 */
function convertSize(size, whiteColorId, noColorId) {
  const oldIds = size.colorToTypes[whiteColorId] || [];
  const noColorTypes = size.colorToTypes[noColorId] || [];

  const newIds = [...oldIds, ...noColorTypes];
  if (newIds.length) {
    size.colorToTypes[whiteColorId] = newIds;
  }
  delete size.colorToTypes[noColorId];
}

/**
 * Replace any color id of "No Color" with the color id of "White" for all upcs
 * @param {Product} product
 */
exports.convertNoColorToWhite = function convertNoColorToWhite(product) {
  const { traits } = product;
  const { upcMap } = traits.traitsMaps;
  const { sizeMap } = traits.sizes;
  const colors = Object.values(traits.colors.colorMap);

  const whiteColorId = findWhiteColorId(colors);
  const noColorIds = findNoColorIds(colors);

  if (whiteColorId && noColorIds.length) {
    Object.keys(upcMap).forEach((upc) => {
      convertUpc(upcMap, upc, whiteColorId, noColorIds);
    });
    Object.values(sizeMap).forEach((size) => {
      convertSize(size, whiteColorId, noColorIds[0]); // TODO assuming there is only 1 "no color" for now
    });
  }
};

/**
 * Select WhiteColorId if current selected color id is no color id
 * @param {Product} product
 */
exports.handleNoColorIdSelected = function handleNoColorIdSelected(product) {
  const { traits } = product;
  const colors = Object.values(traits.colors.colorMap);
  const currentSelectedColorId = String(product.traits.colors.selectedColor);

  const whiteColorId = findWhiteColorId(colors);
  const noColorIds = findNoColorIds(colors);

  if (noColorIds.includes(currentSelectedColorId)) {
    product.traits.colors.selectedColor = Number(whiteColorId);
  }
};

/**
 * @param {number|string|null} [trait]
 * @returns {boolean}
 */
function isDefinedTrait(trait) {
  return typeof trait === 'number' || typeof trait === 'string';
}

function getComplexUpcKey(colorId, sizeOrTypeId, typeId) {
  return [colorId, sizeOrTypeId, typeId]
    .filter(isDefinedTrait)
    .join('_');
}

function isDefaultUpcAvailable({
  product, type, color, size,
}) {
  const complexKey = getComplexUpcKey(color.selectedColor, size.selectedSize, type.selectedTypeId);
  const upcid = product.traits.traitsMaps.upcMap[complexKey];

  const upc = product.relationships.upcs[upcid];
  const availability = upc?.availability;
  return !!(availability?.available || availability?.bopsAvailable);
}

/**
 * This handles the scenario when user selects a color on plp which can break upc availablility on page load.
 * We always need a default upc for 3rd variant.
 * So this selects the first available color for the given size+type combination. It assumes there will always be one.
 * @param {Product} product
 */
exports.handleDefaultColorUnavailable = function handleDefaultColorUnavailable(product) {
  const type = TypeParser(product);
  const color = ColorParser(product);
  const size = SizeParser(product);

  const { selectedSize } = size;

  if (isDefaultUpcAvailable({
    product, type, color, size,
  })) return;

  let newSelectedColor;
  if (typeof type.selectedTypeId === 'number') {
    const sizeToColors = product?.traits?.types?.typeMap[type.selectedTypeId]?.sizeToColors[selectedSize] || [];
    newSelectedColor = color.colorArray.find((c) => sizeToColors.includes(c.id))?.id;
  }

  if (newSelectedColor) {
    product.traits.colors.selectedColor = newSelectedColor;
  }
};
